import { useEffect, useState } from 'react'
import { Controller, FieldValues, Path } from 'react-hook-form'

import { Autocomplete, Icon, Textfield } from '@clientbase/clientbase-library'
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { DataService } from 'api/DataService'
import { AutoCompleteBillingsParams } from 'models/Billings/AutoComplete'

import AutocompleteCustomers from 'components/AutocompleteCustomers'
import GroupOption, { toGroupOption } from 'components/GroupOption'

import { theme } from 'styles/theme'

import { PayersAutocompleteProps } from './PayersAutocomplete.interface'

const PayersAutocomplete = <T extends FieldValues>({
  control,
  setGroupLength,
  nameCustomer,
  setCustomerUuid,
  edit,
  setRecieverType,
  recieverType,
  setGroupUuid,
  watch,
  isUnique
}: PayersAutocompleteProps<T>) => {
  const [groups, setGroups] = useState<AutoCompleteBillingsParams[]>([])

  const getAutocompleteGroups = async () => {
    const responseGroupsAutocomplete = await DataService({
      url: '/v1/customer_groups/autocomplete',
      type: 'GET'
    })

    if (responseGroupsAutocomplete.status === 200) {
      const groupsList: AutoCompleteBillingsParams[] =
        responseGroupsAutocomplete.data.records.map(toGroupOption)

      setGroups(groupsList)
    }
  }

  useEffect(() => {
    if (watch('recieverType' as Path<T>) == 'group') {
      getAutocompleteGroups()
    }
  }, [watch('recieverType' as Path<T>)])

  return (
    <Stack gap={1}>
      <Typography variant="mdBold">Quem você irá cobrar</Typography>

      {edit && nameCustomer && (
        <Textfield placeholder="Buscar cliente" value={nameCustomer} disabled />
      )}

      {!edit && (
        <>
          <Stack direction="row" alignItems="center">
            <Controller
              name={'recieverType' as Path<T>}
              control={control}
              render={({ field }) => (
                <RadioGroup
                  aria-labelledby="group-type"
                  value={field.value}
                  onChange={(event) => {
                    field.onChange(event)
                    !!setRecieverType &&
                      setRecieverType(
                        event.target.value as 'customer' | 'group' | undefined
                      )
                  }}
                  row
                  sx={{ gap: 2 }}
                >
                  <FormControlLabel
                    value="customer"
                    control={<Radio />}
                    label="Cliente"
                  />
                  <FormControlLabel
                    value="group"
                    control={<Radio />}
                    label="Grupo"
                  />
                </RadioGroup>
              )}
            />
            <Tooltip
              enterTouchDelay={0}
              arrow
              placement="right"
              title={
                <span>
                  Ao selecionar essa opção você irá criar uma{' '}
                  {isUnique ? 'cobrança ' : 'recorrência '}
                  para todas as pessoas do grupo selecionado
                </span>
              }
            >
              <Box
                ml="-12px"
                display={'flex'}
                alignItems="center"
                justifyContent={'top'}
              >
                <Icon
                  icon="info"
                  sx={{
                    fontSize: '1.2rem',
                    color: theme.palette.neutral[600],
                    marginTop: '1px'
                  }}
                />
              </Box>
            </Tooltip>
          </Stack>

          {recieverType === 'customer' ? (
            <Controller
              control={control}
              name={'customer' as Path<T>}
              render={({
                field: { onChange, value, ref },
                fieldState: { error }
              }) => {
                const errorTyped = error as any
                return (
                  <AutocompleteCustomers
                    onChange={(_: React.SyntheticEvent, value: unknown) => {
                      const option = value as AutoCompleteBillingsParams
                      setCustomerUuid && setCustomerUuid(option?.value)
                      onChange(option ?? { value: '', label: '' })
                    }}
                    inputRef={ref}
                    value={value}
                    errorMessage={errorTyped?.label?.message}
                    placeholder="Buscar cliente"
                    nameCustomer={nameCustomer}
                    showAddButton={!edit}
                  />
                )
              }}
            />
          ) : null}

          {recieverType === 'group' && (
            <Controller
              control={control}
              name={'group' as Path<T>}
              render={({
                field: { onChange, value },
                fieldState: { error }
              }) => {
                const errorTyped = error as any
                return (
                  <Autocomplete
                    value={value}
                    onChange={(_, value) => {
                      const option = value as AutoCompleteBillingsParams
                      setGroupUuid && setGroupUuid(option?.value ?? undefined)
                      if (option?.customers_count !== undefined) {
                        setGroupLength &&
                          setGroupLength(Number(option.customers_count))
                      }
                      onChange(option ?? { value: '', label: '' })
                    }}
                    renderOption={(props, option) => {
                      const optionTyped = option as AutoCompleteBillingsParams
                      return (
                        <GroupOption liProps={props} option={optionTyped} />
                      )
                    }}
                    options={groups || []}
                    noOptionsText="Nenhum grupo encontrado"
                    renderInput={(params) => (
                      <Textfield
                        helperText={errorTyped?.label?.message as any}
                        error={!!errorTyped?.label?.message as any}
                        placeholder="Buscar grupo"
                        name="group"
                        {...params}
                      />
                    )}
                  />
                )
              }}
            />
          )}
        </>
      )}
    </Stack>
  )
}

export default PayersAutocomplete
